// ValidacionClasificacionPanel.js — informe bajo demanda para comprobar que
// las reglas de clasificación (_lineaNegocio.js) funcionan con datos reales:
// qué series de referencia existen de verdad, ejemplos de factura por línea,
// y el total neto/con IVA por año (para comparar contra una cifra ya
// conocida por otra vía). No se carga automáticamente — es una consulta
// amplia (hasta 2 años de facturas) que no hace falta repetir en cada visita.
import React, { useState } from 'react';
import { authService } from '../../services/authService';

const LINEA_LABEL = {
  mantenimiento: 'Mantenimiento', sat: 'SAT fuera de garantía',
  instalacion: 'Instalaciones', repuestos: 'Repuestos', abono: 'Abonos',
};

function fmtEur(n) {
  if (n == null) return '—';
  return Number(n).toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' €';
}

export default function ValidacionClasificacionPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [desde, setDesde] = useState('2025-01-01');

  const validar = async () => {
    setLoading(true); setError('');
    try {
      const res = await fetch(`/.netlify/functions/getDiagnosticoClasificacion?desde=${desde}`, { headers: { ...authService.authHeader() } });
      const d = await res.json();
      if (!d.success) throw new Error(d.error);
      setData(d);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const anios = data ? Object.keys(data.porAnio).sort() : [];

  return (
    <div className="bg-white rounded-2xl border shadow-sm p-5">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <h3 className="font-bold text-gray-900 text-sm">Validar clasificación (series y ejemplos)</h3>
        <div className="flex items-center gap-2">
          <input type="date" className="border rounded-lg px-2 py-1.5 text-xs" value={desde} onChange={e => setDesde(e.target.value)} />
          <button onClick={validar} disabled={loading} className="px-3 py-2 bg-gray-100 text-gray-700 text-xs font-bold rounded-xl hover:bg-gray-200 disabled:opacity-60">
            {loading ? 'Calculando…' : 'Validar'}
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-2 text-sm mb-3">{error}</div>}

      {!data && !loading && !error && (
        <p className="text-[11px] text-gray-400">Pulsa "Validar" para ver, sobre {data ? '' : 'las facturas reales desde la fecha indicada'}: qué series de referencia existen, 5 ejemplos de factura por línea, y el total neto/con IVA de cada año.</p>
      )}

      {data && (
        <div className="space-y-4">
          <p className="text-[11px] text-gray-400">{data.totalFacturas} facturas entre {data.desde} y {data.hasta}.</p>

          {/* Series encontradas */}
          <div>
            <h4 className="text-xs font-bold text-gray-600 uppercase mb-2">Series de referencia encontradas</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-gray-50/60">
                    <th className="text-left px-3 py-1.5 font-bold text-gray-500 uppercase">Serie</th>
                    <th className="text-right px-3 py-1.5 font-bold text-gray-500 uppercase">Nº facturas</th>
                    <th className="text-left px-3 py-1.5 font-bold text-gray-500 uppercase">Ejemplos</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {data.series.map(s => (
                    <tr key={s.serie}>
                      <td className="px-3 py-1.5 font-mono font-bold text-gray-700">{s.serie}</td>
                      <td className="px-3 py-1.5 text-right">{s.count}</td>
                      <td className="px-3 py-1.5 text-gray-400 font-mono">{s.ejemplos.join(', ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totales por año */}
          <div>
            <h4 className="text-xs font-bold text-gray-600 uppercase mb-2">Totales por año</h4>
            <div className="grid grid-cols-2 gap-3">
              {anios.map(a => (
                <div key={a} className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
                  <p className="text-xs font-bold text-gray-500 uppercase">{a}</p>
                  <p className="text-lg font-bold text-gray-900 mt-1">{fmtEur(data.porAnio[a].facturacionNeta)} <span className="text-xs font-normal text-gray-400">neto</span></p>
                  <p className="text-[11px] text-gray-400">{fmtEur(data.porAnio[a].facturacionConIva)} con IVA</p>
                </div>
              ))}
            </div>
          </div>

          {/* Tabla por línea, por año */}
          <div>
            <h4 className="text-xs font-bold text-gray-600 uppercase mb-2">Por línea</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-gray-50/60">
                    <th className="text-left px-3 py-1.5 font-bold text-gray-500 uppercase">Línea</th>
                    {anios.map(a => <th key={a} colSpan={3} className="text-center px-3 py-1.5 font-bold text-gray-500 uppercase">{a}</th>)}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {Object.keys(LINEA_LABEL).map(l => (
                    <tr key={l}>
                      <td className="px-3 py-1.5 font-semibold text-gray-700">{LINEA_LABEL[l]}</td>
                      {anios.map(a => {
                        const v = data.porAnio[a].porLinea[l];
                        return (
                          <React.Fragment key={a}>
                            <td className="px-2 py-1.5 text-right">{v.count}</td>
                            <td className="px-2 py-1.5 text-right">{fmtEur(v.neta)}</td>
                            <td className="px-2 py-1.5 text-right text-gray-400">{v.pct}%</td>
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Ejemplos por línea */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {Object.keys(LINEA_LABEL).map(l => (
              <div key={l} className="rounded-xl border border-gray-100 px-4 py-3">
                <p className="text-xs font-bold text-gray-500 uppercase mb-2">{LINEA_LABEL[l]} — ejemplos</p>
                {data.ejemplosPorLinea[l].length === 0 ? (
                  <p className="text-[11px] text-gray-300">Sin ejemplos en este rango</p>
                ) : (
                  <ul className="space-y-1.5">
                    {data.ejemplosPorLinea[l].map((e, i) => (
                      <li key={i} className="text-[11px] text-gray-600">
                        <span className="font-mono font-bold">{e.referencia}</span> · {e.fecha} · {fmtEur(e.importeTotal)}{e.esAbono ? ' (abono)' : ''}
                        {e.lineas && <span className="text-gray-400"> — {e.lineas}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
